import { YouTubePlayer } from "youtube-player/dist/types";

export class EmbedVideoPlayer {
    public overlay: Element | null;
    public playBtn: Element | null;
    public player: any;
    public videoContainer: Element;
    public ytIframe: HTMLIFrameElement | null;
    public html5VideoPlayer: HTMLVideoElement | null;

    constructor(videoContainer: Element, videoPlayer: any = null) {
        this.videoContainer = videoContainer;
        this.overlay = videoContainer.querySelector(".video-overlay");
        this.playBtn = videoContainer.querySelector("[data-play-inline]");
        this.ytIframe = videoContainer.querySelector("iframe");
        this.html5VideoPlayer = videoPlayer ?? videoContainer.querySelector("video");
        this.addPlayBtnListener();
        this.addPlayerListeners();
    }

    public isYTPlayer(player: YouTubePlayer): player is YouTubePlayer {
        return !!player?.getVideoEmbedCode;
    }

    public addABTestingClass(interaction = '') {
        this.videoContainer.classList.add("interacted-with");
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'interacted-with',
            'interaction': interaction
        })
    }

    public addPlayerListeners() {
        if (this.html5VideoPlayer) {
            this.player = this.html5VideoPlayer as HTMLVideoElement;
            this.player.addEventListener("ended", () => {
                this.onPlayerEnded();
            });
            this.player.addEventListener("play", () => {
                this.onPlayerPlaying();
            });
            this.player.addEventListener("pause", () => {
                this.addABTestingClass('pause');
            })
            this.player.addEventListener("seeked", () => {
                this.addABTestingClass('seek');
            })
        }
    }

    public addPlayBtnListener() {
        this.playBtn?.addEventListener("click", () => {
            this.addABTestingClass('play')
            this.isYTPlayer(this.player) ? this.player?.playVideo() : this.player?.play();
        });
    }

    public onPlayerEnded = () => {
        setTimeout(() => {
            this.overlay?.classList.remove("video-playing");
            if (this.overlay?.getAttribute("tabIndex")) this.overlay.attributes.removeNamedItem("tabIndex");
            if (this.ytIframe) this.ytIframe.tabIndex = -1;
            if (this.html5VideoPlayer) this.html5VideoPlayer.tabIndex = -1;
        }, 200);
    };

    public onPlayerPlaying() {
        this.overlay?.classList.add("video-playing");
        this.overlay?.setAttribute("tabIndex", "-1");
        if (this.ytIframe) this.ytIframe.tabIndex = 0;
        if (this.html5VideoPlayer) this.html5VideoPlayer.tabIndex = 0;
    }
}

export class EmbedYTiFrame extends EmbedVideoPlayer {
    public ytIframe;

    constructor(videoContainer: Element, iFramePlayer: HTMLIFrameElement) {
        super(videoContainer);
        this.ytIframe = iFramePlayer;
        this.initYTPlayer();
    }

    initYTPlayer = () => {
        if (window.hasOwnProperty("YT") && this.ytIframe) {
            this.player = new window.YT.Player(this.ytIframe, {
                events: {
                    "onStateChange": this.onPlayerStateChange.bind(this),
                    "onAutoplayBlocked": this.onPlayerEnded.bind(this)
                }
            }) as YouTubePlayer;
            this.addIframeListener();
        }
    };

    public addIframeListener() {
        window.addEventListener("blur", () => {
            if (document.activeElement && document.activeElement.id == this.ytIframe.getAttribute("id")) {
                this.addABTestingClass('iframe click')
            }
        });
    }

    public onPlayerStateChange(event: { data: number | any; }) {
        // 2 event equals 'paused' state and 3 event equals 'buffered'
        if (event.data == 2 || event.data == 3) {
            this.addABTestingClass(event.data == 2 ? 'pause' : 'buffer')
        }
        // 0 event equals 'Ended' state
        if (event.data == 0) {
            this.onPlayerEnded();
        } else {
            this.onPlayerPlaying();
        }
    }
}

// Method called on YouTubeAPIFrame ready event
function initYTiFrame() {
    const ytIframePlayers = document.querySelectorAll("[data-inline-video-player] iframe");
    ytIframePlayers.forEach(iframePlayer => {
        const videoContainer = iframePlayer.closest("[data-inline-video-player]");
        if (videoContainer) new EmbedYTiFrame(videoContainer, <HTMLIFrameElement>iframePlayer);
    });
}

// Default init of HTML5 embed players
export default () => {
    const inlineHTMLPlayers = document.querySelectorAll("[data-inline-video-player] video");
    inlineHTMLPlayers.forEach(videoPlayer => {
        const videoContainer = videoPlayer.closest("[data-inline-video-player]");
        if (videoContainer) new EmbedVideoPlayer(videoContainer, videoPlayer);
    });
}

// Attach initYTiFrame method to window to be accessible from root
(window as any)["initYTiFrame"] = initYTiFrame;